import { Link } from "react-router";

import { pluralize } from "@/components/helpers/string-utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Subscription } from "@/types/workspaces";

function SeatsSkeleton() {
  return (
    <Card className="w-full md:w-1/2">
      <CardContent>
        <div className="flex flex-col gap-2">
          <Skeleton className="w-20 h-3.5" />
          <Skeleton className="w-full h-1.5" />
          <Skeleton className="w-36 h-3" />
        </div>
        <Skeleton className="h-5.5 w-24 mt-2" />
      </CardContent>
    </Card>
  );
}

interface WorkspaceSeatsProps {
  numMembers: number;
  totalPendingInvitations?: number | null;
  subscription?: Subscription | null;
  loading?: boolean;
}

export function WorkspaceSeats({
  numMembers,
  totalPendingInvitations,
  subscription,
  loading,
}: WorkspaceSeatsProps) {
  if (loading) return <SeatsSkeleton />;
  if (!subscription) return null;

  const usagePerc = (numMembers / subscription.quantity) * 100;
  const pendingUsagePerc =
    ((totalPendingInvitations || 0) / subscription.quantity) * 100;

  return (
    <Card className="w-full md:w-1/2">
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col w-full">
            <div className="font-semibold">Seats</div>
            <div className="mt-1 bg-gray-200 h-1.5 flex">
              <div
                className={`h-1.5 rounded-sm ${
                  usagePerc > 100 ? "bg-red-600" : "bg-blue-600"
                }`}
                style={{ width: `${Math.min(usagePerc, 100)}%` }}
              />
              {pendingUsagePerc > 0 && (
                <div
                  className="bg-blue-100 h-1.5 rounded-sm"
                  style={{ width: `${Math.min(pendingUsagePerc, 100)}%` }}
                />
              )}
            </div>
            <div className="mt-1 text-gray-600 text-sm">
              <div>
                {numMembers} out of{" "}
                {pluralize({
                  amount: subscription.quantity,
                  singular: "seat",
                  plural: "seats",
                  showCount: true,
                })}{" "}
                used
              </div>
              {(totalPendingInvitations || 0) > 0 && (
                <div className="italic">
                  +
                  {pluralize({
                    amount: totalPendingInvitations,
                    singular: "pending invitation",
                    plural: "pending invitations",
                    showCount: true,
                  })}
                </div>
              )}
            </div>
          </div>
          <Button asChild intent="secondary">
            <Link to="/workspace/billing">Manage</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
